import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useUser } from './UserContext';
import DOMPurify from 'dompurify';

const sanitizeInput = (input) => DOMPurify.sanitize(input);

function LoginPage() {
    const { login } = useUser();
    const [form, setForm] = useState({ username: '', password: '' });
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm({ ...form, [name]: sanitizeInput(value) });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            const res = await fetch('https://company-application-platform-backend.onrender.com/api/auth/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(form)
            });
            const data = await res.json();

            if (res.ok) {
                login(data);
                navigate('/dashboard');
            } else {
                setError(data.error || 'Login fehlgeschlagen');
            }
        } catch (err) {
            setError('Netzwerkfehler beim Login');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <section className="py-12">
                <div>
                    <div className="registration-container">
                        <div className="registration-card">
                            <div className="registration-header">
                                <h1 className="registration-title">Anmelden</h1>
                                <p className="registration-subtitle">
                                    Melden Sie sich an, um auf erweiterte Funktionen und Ihr Dashboard zuzugreifen.
                                </p>
                            </div>
                            {error && (
                                <div className="error-message">
                                    {error}
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="registration-form">
                                <div className="form-grid">
                                    <div className="form-section">
                                        <div className="input-group">
                                            <label htmlFor="kontaktperson" className="input-label">
                                                Benutzername *
                                            </label>
                                            <div className={`input-wrapper`}>
                                                <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                </svg>
                                                <input
                                                    id="username"
                                                    name="username"
                                                    type="text"
                                                    value={form.username}
                                                    onChange={handleChange}
                                                    required
                                                    disabled={loading}
                                                    className="form-input"
                                                    placeholder="Max Mustermann"

                                                />
                                            </div>
                                        </div>
                                        <div className="input-group">
                                            <label htmlFor="passwort" className="input-label">
                                                Passwort *
                                            </label>
                                            <div className={`input-wrapper`}>
                                                <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                </svg>
                                                <input
                                                    type="password"
                                                    id="password"
                                                    name="password"
                                                    value={form.password}
                                                    onChange={handleChange}
                                                    required
                                                    disabled={loading}
                                                    className="form-input"
                                                    placeholder="Mindestens 5 Zeichen"
                                                />
                                                <button
                                                    type="button"
                                                    className="password-toggle"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    disabled={loading}
                                                >
                                                    {showPassword ? (
                                                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                                        </svg>
                                                    ) : (
                                                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                        </svg>
                                                    )}
                                                </button>
                                            </div>
                                            <p className="input-hint">Mindestens 5 Zeichen</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="form-footer">
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className={`submit-button ${loading ? 'loading' : ''}`}
                                    >
                                        {loading ? (
                                            <>
                                                <svg className="loading-spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Anmeldung läuft...
                                            </>
                                        ) : 'Anmelden'}
                                    </button>
                                </div>
                            </form>

                            <div className="form-footer-links">
                                <p className="footer-text">
                                    Noch kein Account? (
                                    <Link to="/register" className="footer-link">
                                        Hier registrieren
                                    </Link>
                                    )
                                </p>
                                <p className="footer-text">
                                    <Link to="/" className="footer-link">
                                        Zurück zur Startseite
                                    </Link>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

export default LoginPage;